import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { DateValue } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { classNames } from "@/utils/Classnames";
import { useGetDrivers, putReassignDriver, putUpdatePickupDate } from "@/services/LogisticsCoordinator";
import { Collection } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import styles from "./styles.module.css";
import shared from "../styles.module.css";
import { Avatar, formatPlanned, SectionTitle, StatusBadge, initialsOf } from "../shared";

type Editing = null | "time" | "driver";

/**
 * Scheduled detail panel. `COMPLETED` collections are read-only. Date/driver changes hit the
 * transport endpoints and invalidate the collections query. Mounted with `key={collection.id}`
 * so the editing sub-panel resets when a different row is selected.
 */
const ScheduledDetail: FC<{ collection: Collection }> = ({ collection }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing>(null);
  const [chosenDate, setChosenDate] = useState<DateValue | undefined>(() =>
    collection.plannedDate ? new Date(collection.plannedDate) : null
  );

  const plannedDateISO = chosenDate ? dayjs(chosenDate).format("YYYY-MM-DD") : "";
  const isLocked = collection.status === "COMPLETED";
  const canEdit = !isLocked;

  const { data: driverData } = useGetDrivers(plannedDateISO, {
    enabled: editing === "driver" && !!plannedDateISO,
  });
  const drivers = driverData?.drivers ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["getCollections"] });

  const updatePickupDate = useMutation({
    mutationFn: () => putUpdatePickupDate(collection.id, dayjs(chosenDate).toISOString()),
    onSuccess: () => {
      setEditing(null);
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("containers:toast.timeSaved", { day: formatPlanned(plannedDateISO) })
        )
      );
      invalidate();
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.scheduleError")));
    },
  });

  const reassignDriver = useMutation({
    mutationFn: (userId: number) => putReassignDriver(collection.id, userId),
    onSuccess: (_data, userId) => {
      setEditing(null);
      const name = drivers.find((d) => d.userId === userId)?.name ?? "";
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("containers:toast.reassigned", { name }))
      );
      invalidate();
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.assignError")));
    },
  });

  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
        <div className={shared.detailHeaderText}>
          <Text size="sm">
            {collection.wasteTypeName} · {collection.siteName}
          </Text>
          <div style={{ marginTop: 7 }}>
            <StatusBadge status={collection.status} />
          </div>
        </div>
      </div>

      <div className={shared.detailBody}>
        {/* Planned date */}
        <div className={shared.detailSectionHeader}>
          <SectionTitle>{t("containers:detail.plannedTime")}</SectionTitle>
          {canEdit && editing !== "time" && (
            <button type="button" className={shared.linkBtn} onClick={() => setEditing("time")}>
              {t("actions:change")}
            </button>
          )}
        </div>

        {editing !== "time" && (
          <div className={styles.slotCard} style={{ marginTop: 12 }}>
            <SVGAsset name="calendar" width={24} height={24} style={{ opacity: 0.85 }} />
            <Text size="md">{formatPlanned(collection.plannedDate)}</Text>
          </div>
        )}

        {editing === "time" && (
          <div className={shared.editPanel} style={{ marginTop: 12 }}>
            <DatePickerInput
              label={t("containers:detail.pickDate")}
              placeholder={t("actions:selectDate")}
              value={chosenDate}
              onChange={setChosenDate}
              minDate={new Date()}
            />
            <div className={styles.warningNote} style={{ marginTop: 14 }}>
              <SVGAsset name="info" width={15} height={15} style={{ marginTop: 1, opacity: 0.7 }} />
              <Text size="sm" color="tertiary" style={{ lineHeight: 1.45 }}>
                {t("containers:detail.driverNotifiedTime")}
              </Text>
            </div>
            <div className={styles.editActions} style={{ marginTop: 14 }}>
              <CTA
                intent="primary"
                size="lg"
                style={{ flex: 1 }}
                disabled={!chosenDate || updatePickupDate.isPending}
                onClick={() => updatePickupDate.mutate()}
              >
                {t("containers:detail.saveNotify")}
              </CTA>
              <CTA intent="secondary" size="lg" onClick={() => setEditing(null)}>
                {t("actions:cancel")}
              </CTA>
            </div>
          </div>
        )}

        {/* Assigned driver */}
        <div className={shared.divider}>
          <div className={shared.detailSectionHeader}>
            <SectionTitle>{t("containers:detail.assignedDriver")}</SectionTitle>
            {canEdit && editing !== "driver" && (
              <button type="button" className={shared.linkBtn} onClick={() => setEditing("driver")}>
                {t("containers:detail.reassign")}
              </button>
            )}
          </div>

          {editing !== "driver" && (
            <div className={styles.slotCard} style={{ marginTop: 12 }}>
              <Avatar initials={initialsOf(collection.assignedDriverName)} size={38} />
              <div style={{ flex: 1 }}>
                <Text size="md" strong>
                  {collection.assignedDriverName}
                </Text>
                {/* TODO: truck · context line — not provided by the API yet. */}
              </div>
            </div>
          )}

          {editing === "driver" && (
            <div className={shared.driverList} style={{ marginTop: 12 }}>
              {drivers.map((d) => {
                const isCurrent = d.userId === collection.assignedDriverUserId;
                const selectable = d.available || isCurrent;
                return (
                  <button
                    key={d.userId}
                    type="button"
                    disabled={!selectable}
                    onClick={selectable ? () => reassignDriver.mutate(d.userId) : undefined}
                    className={classNames(
                      shared.driverRow,
                      isCurrent ? shared.driverRowRecommended : "",
                      selectable ? "" : shared.driverRowDisabled
                    )}
                  >
                    <Avatar initials={initialsOf(d.name)} size={34} variant={selectable ? "active" : "disabled"} />
                    <span className={shared.driverMeta}>
                      <span className={shared.driverName}>
                        <Text span size="sm" strong color={selectable ? "primary" : "secondary"}>
                          {d.name}
                        </Text>
                        {isCurrent && <span className={shared.driverTag}>{t("containers:driver.current")}</span>}
                      </span>
                    </span>
                    <Text
                      span
                      display="technical"
                      size="xs"
                      tt="uppercase"
                      color={d.available ? "success" : "secondary"}
                    >
                      {d.available ? t("containers:driver.available") : t("containers:driver.full")}
                    </Text>
                  </button>
                );
              })}
              <div className={styles.warningNote} style={{ marginTop: 4 }}>
                <SVGAsset name="info" width={15} height={15} style={{ marginTop: 1, opacity: 0.7 }} />
                <Text size="sm" color="tertiary" style={{ lineHeight: 1.45 }}>
                  {t("containers:detail.driverNotifiedReassign")}
                </Text>
              </div>
            </div>
          )}
        </div>

        {isLocked && (
          <div
            className={`${styles.resultBanner} ${styles.bannerCompleted}`}
            style={{ marginTop: 18, padding: "12px 14px" }}
          >
            <SVGAsset name="checkmark-green" width={16} height={16} />
            <Text size="sm" color="success">
              {t("containers:detail.completedLocked")}
            </Text>
          </div>
        )}
      </div>
    </aside>
  );
};

export default ScheduledDetail;
